import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { MonetizationTariff } from 'types/enums';
import baseStyles from '../style.module.scss';
import { PremiumTariffForwardRefProps, TariffBaseProps } from '../types';
import styles from './style.module.scss';

type Props = Pick<
  TariffBaseProps<MonetizationTariff.Premium>,
  'items' | 'setPremiumProduct' | 'hidePerVacancyPrice'
>;

const PremiumTariff = forwardRef<PremiumTariffForwardRefProps, Props>(
  ({ items, setPremiumProduct, hidePerVacancyPrice }, ref) => {
    const t = useTranslations();

    const premiumProducts = useMemo(() => {
      return items
        .filter(
          (item) =>
            item.status.toLowerCase() === 'active' &&
            item.contents.tariff === MonetizationTariff.Premium,
        )
        .sort((a, b) => a.contents.totalCount - b.contents.totalCount);
    }, [items]);

    const [valuePremium, setValuePremium] = useState(
      premiumProducts && Object.keys(premiumProducts).length ? 0 : null,
    );

    useImperativeHandle(ref, () => {
      return {
        getPremiumProducts: () => premiumProducts,
        getPremiumValue: () => valuePremium,
      };
    }, [premiumProducts, valuePremium]);

    useEffect(() => {
      setValuePremium(premiumProducts && Object.keys(premiumProducts).length ? 0 : null);
    }, [premiumProducts]);

    useEffect(() => {
      if (valuePremium === null) return;

      setPremiumProduct(premiumProducts[valuePremium]);
    }, [valuePremium]);

    if (typeof valuePremium !== 'number') {
      return <span>loading</span>;
    }

    return (
      <div className={styles.premiumContainer}>
        {premiumProducts.map((product, idx) => (
          <div
            key={product.id}
            className={classNames(styles.choice, {
              [styles.choice__active]: valuePremium === idx,
            })}
            onClick={() => setValuePremium(idx)}>
            <p className={styles.placementsPremium}>
              <span className={styles.numberPremium}>
                {t('placements', { count: product.contents.totalCount }).split(' ')[0]}
              </span>{' '}
              {t('placements', { count: product.contents.totalCount }).split(' ')[1]}
            </p>
            <div className={styles.pricesPremium}>
              <span className={baseStyles.price}>
                {product.contents.meta.currencySymbol}
                {product.contents.price}
              </span>
              <span className={baseStyles.oldPrice}>
                {product.contents.meta.currencySymbol}
                {product.contents.meta.oldPrice}
              </span>
              <div className={baseStyles.badge}>
                -
                {product.contents.meta.discount
                  ? product.contents.meta.discount
                  : ((product.contents.meta.oldPrice - product.contents.price) /
                      product.contents.meta.oldPrice) *
                    100}
                %
              </div>
            </div>
            {idx === 0 && hidePerVacancyPrice ? null : (
              <div className={baseStyles.perVacancy}>
                {product.contents.meta.currencySymbol}
                {t('price_for_vacancies', {
                  count: Math.floor(product.contents.price / product.contents.totalCount),
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  },
);

PremiumTariff.displayName = 'PremiumTariff';

export default PremiumTariff;
